import { useEffect, useState } from 'react';
import { useQuill } from 'react-quilljs';
import { useDebounceCallback } from 'usehooks-ts';

const theme = 'snow';

const modules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};
const formats = [
  'bold',
  'italic',
  'underline',
  'list',
  'indent',
  'link',
  'header',
];

export type RichTextEditorProps = {
  disabled?: boolean;
  name: string;
  onChange: (value: string) => void;
  placeholder?: string;
  value?: string;
};

export const RichTextEditor = ({
  onChange,
  disabled,
  placeholder,
  value,
}: RichTextEditorProps) => {
  const [richTextValue, setRichTextValue] = useState('');

  const { quill, quillRef } = useQuill({
    theme,
    modules,
    formats,
    placeholder,
    readOnly: disabled,
  });

  const callback = useDebounceCallback(() => {
    const textContent = quillRef.current.firstChild.textContent;
    onChange(textContent ? quillRef.current.firstChild.innerHTML : '');
    setRichTextValue(quillRef.current.firstChild.innerHTML);
  }, 500);

  useEffect(() => {
    if (quill) {
      quill.on('text-change', () => {
        callback();
      });
    }
  }, [quill, callback]);

  useEffect(() => {
    // make sure we set the initial value & reset it when changes are discarded
    if (quill && value && value !== richTextValue) {
      quill.clipboard.dangerouslyPasteHTML(value);
      setRichTextValue(value);
    }
  }, [quill, value, richTextValue]);

  return (
    <div className="border-neutral-250 flex w-full grow flex-col overflow-hidden rounded-md border bg-neutral-50 p-3 shadow-none xl:p-6">
      <div
        className="rich-text-content max-h-[12.5vh] grow overflow-x-hidden overflow-y-auto px-2.5"
        ref={quillRef}
      />
    </div>
  );
};
